/**
 * Platform-agnostic branding. This is the single source of truth a client
 * edits. Adapters project it onto MUI (web) and StyleSheet (React Native).
 */
export interface Tokens {
  color: {
    primary: string
    primaryContrast: string
    secondary: string
    secondaryContrast: string
    error: string
    warning: string
    info: string
    success: string
    surface: string
    background: string
    textPrimary: string
    textSecondary: string
    textDisabled: string
  }
  radius: { sm: number; md: number; lg: number }
  spacing: { unit: number }
  font: { family: string; scale: number }
  mode: 'light' | 'dark'
  logoUrl: string
}

export const DEFAULT_TOKENS: Tokens = {
  color: {
    primary: '#1976D2',
    primaryContrast: '#FFFFFF',
    secondary: '#9C27B0',
    secondaryContrast: '#FFFFFF',
    error: '#D32F2F',
    warning: '#ED6C02',
    info: '#0288D1',
    success: '#2E7D32',
    surface: '#FFFFFF',
    background: '#FAFAFA',
    textPrimary: 'rgba(0, 0, 0, 0.87)',
    textSecondary: 'rgba(0, 0, 0, 0.6)',
    textDisabled: 'rgba(0, 0, 0, 0.38)',
  },
  radius: { sm: 4, md: 8, lg: 16 },
  spacing: { unit: 8 },
  font: { family: 'Roboto, system-ui, sans-serif', scale: 1 },
  mode: 'light',
  logoUrl: '',
}

/**
 * The dark counterpart. `Tokens` holds one fully-resolved colour per role and
 * carries `mode` alongside them, which means `mode` on its own changes nothing —
 * a storefront that sets `mode: 'dark'` against the light defaults renders
 * black text on a dark surface. Verified in a browser before this existed.
 *
 * Only the mode-dependent slots differ: the surfaces and the text ramp. The
 * brand and semantic colours are the client's and are not second-guessed here.
 * Values match MUI's own dark palette so an unbranded dark storefront looks the
 * way MUI intends.
 */
export const DARK_TOKENS: Tokens = {
  ...DEFAULT_TOKENS,
  mode: 'dark',
  color: {
    ...DEFAULT_TOKENS.color,
    surface: '#121212',
    background: '#121212',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    textDisabled: 'rgba(255, 255, 255, 0.5)',
  },
}

/**
 * The baseline to layer a storefront's overrides onto. Callers must resolve the
 * mode *before* picking a baseline, because the baseline depends on it.
 */
export const defaultTokensFor = (mode: Tokens['mode']): Tokens =>
  mode === 'dark' ? DARK_TOKENS : DEFAULT_TOKENS
